import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Upload, Eye, X, ChevronLeft, ChevronRight, Edit, Check, MessageSquare, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { formatDate } from '@/utils/dateFormat';
import { useCurrency } from '@/contexts/CurrencyContext';
import { BankCategoryBreakdown } from '@/components/BankCategoryBreakdown';
import { UnmatchedDeposits } from '@/components/UnmatchedDeposits';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const AccessBankReconciliation = () => {
  const [depositEntries, setDepositEntries] = useState([]);
  const [paymentEntries, setPaymentEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingLines, setSavingLines] = useState({}); // Track which lines are being saved
  const [viewReceiptOpen, setViewReceiptOpen] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [editingDepositLine, setEditingDepositLine] = useState(null);
  const [editingPaymentLine, setEditingPaymentLine] = useState(null);
  const [descriptionDialog, setDescriptionDialog] = useState({ open: false, type: '', lineNumber: null, value: '' });
  const [runningDepositTotal, setRunningDepositTotal] = useState(0);
  const [runningPaymentTotal, setRunningPaymentTotal] = useState(0);
  const [previousMonthCarryForward, setPreviousMonthCarryForward] = useState(0);
  const { currencySymbol } = useCurrency();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser?.role === 'admin';
  
  // Auto-save debounce refs
  const autoSaveTimers = useRef({});
  const pendingChanges = useRef({});
  // Refs to hold latest entries for auto-save callback
  const depositEntriesRef = useRef(depositEntries);
  const paymentEntriesRef = useRef(paymentEntries);
  
  // Keep refs updated with latest state
  useEffect(() => {
    depositEntriesRef.current = depositEntries;
  }, [depositEntries]);
  
  useEffect(() => {
    paymentEntriesRef.current = paymentEntries;
  }, [paymentEntries]);

  const calculateCharges = (amount) => {
    if (!amount || amount <= 0) return 0;
    if (amount < 10000) return 10.75;
    if (amount < 50000) return 76.88;
    return 103.75;
  };

  const initializeEntries = (existingEntries, entryType) => {
    const newEntries = [];
    for (let i = 1; i <= 20; i++) {
      const existing = existingEntries.find(e => e.line_number === i);
      if (existing) {
        newEntries.push({ ...existing, bank: 'access_bank' });
      } else {
        newEntries.push({
          id: null,
          date: selectedDate,
          entry_type: entryType,
          line_number: i,
          bank: 'access_bank',
          access_bank: { 
            amount: 0, 
            description: '', 
            receipt_url: null, 
            charges: 0, 
            fund_movement: 0, 
            from_source: '',
            bank_adj: 0,
            total_charges: 0,
            hidden_charges: 0
          }
        });
      }
    }
    return newEntries;
  };

  useEffect(() => {
    fetchEntries();
    fetchMonthlyRunningTotals();
  }, [selectedDate]);

  const fetchMonthlyRunningTotals = async () => {
    try {
      // Get the first day of the month for the selected date
      const selectedDateObj = new Date(selectedDate);
      const firstDayOfMonth = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), 1);
      const firstDayStr = firstDayOfMonth.toISOString().split('T')[0];
      
      // Get the last day of the previous month (for carry forward calculation)
      const lastDayPrevMonth = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), 0);
      const lastDayPrevMonthStr = lastDayPrevMonth.toISOString().split('T')[0];
      
      // Fetch all entries for access_bank only
      const [depositRes, paymentRes] = await Promise.all([
        axios.get(`${API}/reconciliation/deposit?bank=access_bank`, getAuthHeader()),
        axios.get(`${API}/reconciliation/payment_charge?bank=access_bank`, getAuthHeader())
      ]);
      
      // Filter entries by date range
      const filterByDateRange = (entries, startDate, endDate) => entries.filter(e => {
        const entryDate = e.date;
        return entryDate >= startDate && entryDate <= endDate;
      });
      
      // Filter entries up to a specific date (for carry forward - all entries BEFORE current month)
      const filterUpToDate = (entries, endDate) => entries.filter(e => {
        const entryDate = e.date;
        return entryDate <= endDate;
      });
      
      const monthDeposits = filterByDateRange(depositRes.data, firstDayStr, selectedDate);
      const monthPayments = filterByDateRange(paymentRes.data, firstDayStr, selectedDate);
      
      // Calculate current month running totals for deposits
      const depositRunningTotal = monthDeposits.reduce((sum, e) => {
        const amount = e.access_bank?.amount || 0;
        const fundMvt = e.access_bank?.fund_movement || 0;
        const bankAdj = e.access_bank?.bank_adj || 0;
        return sum + amount + fundMvt + bankAdj;
      }, 0);
      
      // Calculate current month running totals for payments
      const paymentRunningTotal = monthPayments.reduce((sum, e) => {
        const amount = e.access_bank?.amount || 0;
        const charges = e.access_bank?.charges || 0;
        const hiddenCharges = e.access_bank?.hidden_charges || 0;
        const fundMvt = e.access_bank?.fund_movement || 0;
        const totalCharges = charges + hiddenCharges;
        return sum + amount + totalCharges + fundMvt;
      }, 0);
      
      // Calculate cumulative carry forward from ALL previous months (not just the immediate previous month)
      // This ensures the Net balance reflects the true amount on hand
      const allPrevDeposits = filterUpToDate(depositRes.data, lastDayPrevMonthStr);
      const allPrevPayments = filterUpToDate(paymentRes.data, lastDayPrevMonthStr);
      
      const allPrevDepositTotal = allPrevDeposits.reduce((sum, e) => {
        const amount = e.access_bank?.amount || 0;
        const fundMvt = e.access_bank?.fund_movement || 0;
        const bankAdj = e.access_bank?.bank_adj || 0;
        return sum + amount + fundMvt + bankAdj;
      }, 0);
      
      const allPrevPaymentTotal = allPrevPayments.reduce((sum, e) => {
        const amount = e.access_bank?.amount || 0;
        const charges = e.access_bank?.charges || 0;
        const hiddenCharges = e.access_bank?.hidden_charges || 0;
        const fundMvt = e.access_bank?.fund_movement || 0;
        const totalCharges = charges + hiddenCharges;
        return sum + amount + totalCharges + fundMvt;
      }, 0);
      
      const cumulativeCarryForward = allPrevDepositTotal - allPrevPaymentTotal;
      
      setRunningDepositTotal(depositRunningTotal);
      setRunningPaymentTotal(paymentRunningTotal);
      setPreviousMonthCarryForward(cumulativeCarryForward);
    } catch (error) {
      setRunningDepositTotal(0);
      setRunningPaymentTotal(0);
      setPreviousMonthCarryForward(0);
    }
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const [depositRes, paymentRes] = await Promise.all([
        axios.get(`${API}/reconciliation/deposit?date=${selectedDate}&bank=access_bank`, getAuthHeader()),
        axios.get(`${API}/reconciliation/payment_charge?date=${selectedDate}&bank=access_bank`, getAuthHeader())
      ]);
      setDepositEntries(initializeEntries(depositRes.data, 'deposit'));
      setPaymentEntries(initializeEntries(paymentRes.data, 'payment_charge'));
    } catch (error) {
      toast.error('Failed to fetch entries');
      setDepositEntries(initializeEntries([], 'deposit'));
      setPaymentEntries(initializeEntries([], 'payment_charge'));
    } finally {
      setLoading(false);
    }
  };

  const handleDepositChange = (lineNumber, field, value) => {
    setDepositEntries(prev => prev.map(entry => {
      if (entry.line_number === lineNumber) {
        // Handle numeric fields - keep as string while typing to support decimal input
        const isNumericField = ['amount', 'fund_movement', 'bank_adj', 'total_charges'].includes(field);
        const processedValue = isNumericField ? (value === '' ? 0 : parseFloat(value) || 0) : value;
        const newBankData = { ...entry.access_bank, [field]: processedValue };
        return { ...entry, access_bank: newBankData };
      }
      return entry;
    }));
    // Trigger auto-save
    triggerAutoSave(lineNumber, 'deposit');
  };

  const handlePaymentChange = (lineNumber, field, value) => {
    setPaymentEntries(prev => prev.map(entry => {
      if (entry.line_number === lineNumber) {
        // Handle numeric fields - keep as string while typing to support decimal input
        const isNumericField = ['amount', 'fund_movement', 'charges', 'hidden_charges', 'total_charges'].includes(field);
        const processedValue = isNumericField ? (value === '' ? 0 : parseFloat(value) || 0) : value;
        if (field === 'amount') {
          return {
            ...entry,
            access_bank: { ...entry.access_bank, amount: processedValue, charges: calculateCharges(processedValue) }
          };
        }
        return { ...entry, access_bank: { ...entry.access_bank, [field]: processedValue } };
      }
      return entry;
    }));
    // Trigger auto-save
    triggerAutoSave(lineNumber, 'payment');
  };

  const getDepositTotal = (entry) => {
    const amount = entry.access_bank?.amount || 0;
    const fundMvt = entry.access_bank?.fund_movement || 0;
    const bankAdj = entry.access_bank?.bank_adj || 0;
    return amount + fundMvt + bankAdj;
  };

  const getPaymentTotal = (entry) => {
    const amount = entry.access_bank?.amount || 0;
    const charges = entry.access_bank?.charges || 0;
    const hiddenCharges = entry.access_bank?.hidden_charges || 0;
    const fundMvt = entry.access_bank?.fund_movement || 0;
    const totalCharges = charges + hiddenCharges;
    return amount + totalCharges + fundMvt;
  };

  const getLineTotalCharges = (entry) => {
    const charges = entry.access_bank?.charges || 0;
    const hiddenCharges = entry.access_bank?.hidden_charges || 0;
    return charges + hiddenCharges;
  };

  const saveEntry = async (entry, type, isAutoSave = false) => {
    const bankData = entry.access_bank;
    // Check if any meaningful data exists (including description)
    const hasData = bankData?.amount > 0 || bankData?.description || bankData?.fund_movement !== 0 || bankData?.from_source || bankData?.bank_adj !== 0 || bankData?.charges > 0 || bankData?.hidden_charges > 0;
    if (!hasData) return;

    const lineKey = `${type}-${entry.line_number}`;
    setSavingLines(prev => ({ ...prev, [lineKey]: true }));
    
    try {
      if (entry.id) {
        await axios.put(`${API}/reconciliation/${entry.id}`, entry, getAuthHeader());
      } else {
        const response = await axios.post(`${API}/reconciliation`, entry, getAuthHeader());
        if (type === 'deposit') {
          setDepositEntries(prev => prev.map(e => e.line_number === entry.line_number ? { ...e, id: response.data.id } : e));
        } else {
          setPaymentEntries(prev => prev.map(e => e.line_number === entry.line_number ? { ...e, id: response.data.id } : e));
        }
      }
      if (!isAutoSave) {
        toast.success('Entry saved');
        if (type === 'deposit') setEditingDepositLine(null);
        else setEditingPaymentLine(null);
      }
      // Refresh running totals immediately
      fetchMonthlyRunningTotals();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' ? errorDetail : 
                          Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || e).join(', ') :
                          errorDetail?.msg || 'Failed to save entry';
      toast.error(errorMessage);
    } finally {
      setSavingLines(prev => ({ ...prev, [lineKey]: false }));
    }
  };

  // Auto-save function with debouncing - 3 second delay for better UX
  const triggerAutoSave = useCallback((lineNumber, type) => {
    const lineKey = `${type}-${lineNumber}`;
    
    // Clear any existing timer for this line
    if (autoSaveTimers.current[lineKey]) {
      clearTimeout(autoSaveTimers.current[lineKey]);
    }
    
    // Set a new timer to save after 3 seconds of inactivity
    autoSaveTimers.current[lineKey] = setTimeout(() => {
      // Use refs to get the latest entries (avoids stale closure issue)
      const entries = type === 'deposit' ? depositEntriesRef.current : paymentEntriesRef.current;
      const entry = entries.find(e => e.line_number === lineNumber);
      if (entry) {
        saveEntry(entry, type, true);
      }
    }, 3000);
  }, []);

  const deleteEntry = async (entry, type) => {
    if (!entry.id) return;
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API}/reconciliation/${entry.id}`, getAuthHeader());
      toast.success('Entry deleted');
      fetchEntries();
      // Refresh running totals immediately
      fetchMonthlyRunningTotals();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleReceiptUpload = async (entryId, file, type) => {
    if (!entryId) {
      toast.error('Please save the entry first');
      return;
    }
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please upload an image or PDF file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await axios.post(`${API}/reconciliation/${entryId}/receipt/access_bank`, { receipt: reader.result }, getAuthHeader());
        toast.success('Receipt uploaded');
        fetchEntries();
      } catch (error) {
        toast.error('Failed to upload receipt');
      }
    };
    reader.readAsDataURL(file);
  };

  const deleteReceipt = async (entryId) => {
    if (!window.confirm('Delete this receipt?')) return;
    try {
      await axios.post(`${API}/reconciliation/${entryId}/receipt/access_bank`, { receipt: null }, getAuthHeader());
      toast.success('Receipt deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete receipt');
    }
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const openDescriptionDialog = (type, lineNumber, currentValue) => {
    setDescriptionDialog({ open: true, type, lineNumber, value: currentValue || '' });
  };

  const saveDescription = () => {
    const { type, lineNumber, value } = descriptionDialog;
    if (type === 'deposit') {
      handleDepositChange(lineNumber, 'description', value);
    } else {
      handlePaymentChange(lineNumber, 'description', value);
    }
    setDescriptionDialog({ open: false, type: '', lineNumber: null, value: '' });
  };

  const calculateDepositTotal = () => depositEntries.reduce((sum, e) => sum + getDepositTotal(e), 0);
  const calculatePaymentTotal = () => paymentEntries.reduce((sum, e) => sum + getPaymentTotal(e), 0);
  const calculateChargesTotal = () => paymentEntries.reduce((sum, e) => sum + (e.access_bank?.charges || 0), 0);
  const getDepositsMTDWithCarryForward = () => runningDepositTotal + previousMonthCarryForward;
  const calculateNetBalance = () => getDepositsMTDWithCarryForward() - runningPaymentTotal;

  const renderDepositRow = (entry) => {
    const bankData = entry.access_bank || {};
    const editing = isAdmin && (editingDepositLine === entry.line_number || !entry.id);
    const lineTotal = getDepositTotal(entry);
    
    return (
      <tr key={entry.line_number} className="border-t hover:bg-blue-50/30">
        <td className="p-1 border-r text-center text-xs text-slate-500">{entry.line_number}</td>
        <td className="p-1 border-r" title={bankData.amount ? `Amount: ${currencySymbol}${parseFloat(bankData.amount).toLocaleString()}` : ''}>
          <Input type="number" value={bankData.amount || ''} onChange={(e) => handleDepositChange(entry.line_number, 'amount', e.target.value)}
            className="w-20 h-7 text-xs text-right" placeholder="0.00" disabled={!editing} 
            title={bankData.amount ? `Amount: ${currencySymbol}${parseFloat(bankData.amount).toLocaleString()}` : 'Enter amount'} />
        </td>
        <td className="p-1 border-r">
          <button
            disabled={!isAdmin}
            onClick={() => openDescriptionDialog('deposit', entry.line_number, bankData.description)}
            className="w-full h-7 text-xs text-left px-2 border rounded bg-white hover:bg-slate-50 truncate flex items-center gap-1"
            title={bankData.description || 'Click to add description'}
          >
            <MessageSquare className="w-3 h-3 text-slate-400 flex-shrink-0" />
            <span className="truncate">{bankData.description || 'Add...'}</span>
          </button>
        </td>
        <td className="p-1 border-r text-center">
          {bankData.receipt_url ? (
            <div className="flex items-center justify-center gap-1">
              <button onClick={() => { setViewingReceipt(bankData.receipt_url); setViewReceiptOpen(true); }} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Eye className="w-3 h-3" /></button>
              {isAdmin && <button onClick={() => deleteReceipt(entry.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><X className="w-3 h-3" /></button>}
            </div>
          ) : isAdmin ? (
            <label className="cursor-pointer">
              <input type="file" accept="image/*,.pdf" onChange={(e) => { if (e.target.files[0]) handleReceiptUpload(entry.id, e.target.files[0], 'deposit'); e.target.value = ''; }} className="hidden" />
              <span className="p-1 text-slate-400 hover:text-emerald-600"><Upload className="w-3 h-3" /></span>
            </label>
          ) : (
            <span className="text-xs text-slate-300">-</span>
          )}
        </td>
        <td className="p-1 border-r" title={bankData.bank_adj ? `Bank Adj: ${currencySymbol}${parseFloat(bankData.bank_adj).toLocaleString()}` : ''}>
          <Input type="number" value={bankData.bank_adj || ''} onChange={(e) => handleDepositChange(entry.line_number, 'bank_adj', e.target.value)}
            className="w-16 h-7 text-xs text-right" placeholder="0.00" disabled={!editing}
            title={bankData.bank_adj ? `Bank Adj: ${currencySymbol}${parseFloat(bankData.bank_adj).toLocaleString()}` : 'Enter bank adjustment'} />
        </td>
        <td className="p-1 border-r">
          <Input type="number" value={bankData.fund_movement || ''} onChange={(e) => handleDepositChange(entry.line_number, 'fund_movement', e.target.value)}
            className="w-16 h-7 text-xs text-right" placeholder="0.00" disabled={!editing} />
        </td>
        <td className="p-1 border-r">
          <Input value={bankData.from_source || ''} onChange={(e) => handleDepositChange(entry.line_number, 'from_source', e.target.value)}
            className="w-20 h-7 text-xs" placeholder="Source..." disabled={!editing} />
        </td>
        <td className="p-1 border-r text-center">
          <div className="w-16 h-7 text-xs text-right bg-emerald-50 border rounded px-2 flex items-center justify-end font-semibold text-emerald-700">
            {lineTotal > 0 ? lineTotal.toLocaleString() : '-'}
          </div>
        </td>
        <td className="p-1 text-center">
          <div className="flex justify-center gap-1">
            {savingLines[`deposit-${entry.line_number}`] ? (
              <Loader2 className="w-3 h-3 animate-spin text-emerald-600" />
            ) : !isAdmin ? (
              <span className="text-xs text-slate-400">View only</span>
            ) : entry.id ? (
              <>
                {editingDepositLine === entry.line_number ? (
                  <button onClick={() => setEditingDepositLine(null)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Check className="w-3 h-3" /></button>
                ) : (
                  <button onClick={() => setEditingDepositLine(entry.line_number)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-3 h-3" /></button>
                )}
                <button onClick={() => deleteEntry(entry, 'deposit')} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3" /></button>
              </>
            ) : (
              <span className="text-xs text-slate-400">Auto-save</span>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const renderPaymentRow = (entry) => {
    const bankData = entry.access_bank || {};
    const editing = isAdmin && (editingPaymentLine === entry.line_number || !entry.id);
    const lineTotal = getPaymentTotal(entry);
    const lineTotalCharges = getLineTotalCharges(entry);
    
    return (
      <tr key={entry.line_number} className="border-t hover:bg-purple-50/30">
        <td className="p-1 border-r text-center text-xs text-slate-500">{entry.line_number}</td>
        <td className="p-1 border-r" title={bankData.amount ? `Amount: ${currencySymbol}${parseFloat(bankData.amount).toLocaleString()}` : ''}>
          <Input type="number" value={bankData.amount || ''} onChange={(e) => handlePaymentChange(entry.line_number, 'amount', e.target.value)}
            className="w-20 h-7 text-xs text-right" placeholder="0.00" disabled={!editing}
            title={bankData.amount ? `Amount: ${currencySymbol}${parseFloat(bankData.amount).toLocaleString()}` : 'Enter amount'} />
        </td>
        <td className="p-1 border-r">
          <button
            disabled={!isAdmin}
            onClick={() => openDescriptionDialog('payment', entry.line_number, bankData.description)}
            className="w-full h-7 text-xs text-left px-2 border rounded bg-white hover:bg-slate-50 truncate flex items-center gap-1"
            title={bankData.description || 'Click to add description'}
          >
            <MessageSquare className="w-3 h-3 text-slate-400 flex-shrink-0" />
            <span className="truncate">{bankData.description || 'Add...'}</span>
          </button>
        </td>
        <td className="p-1 border-r text-center">
          {bankData.receipt_url ? (
            <div className="flex items-center justify-center gap-1">
              <button onClick={() => { setViewingReceipt(bankData.receipt_url); setViewReceiptOpen(true); }} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Eye className="w-3 h-3" /></button>
              {isAdmin && <button onClick={() => deleteReceipt(entry.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><X className="w-3 h-3" /></button>}
            </div>
          ) : isAdmin ? (
            <label className="cursor-pointer">
              <input type="file" accept="image/*,.pdf" onChange={(e) => { if (e.target.files[0]) handleReceiptUpload(entry.id, e.target.files[0], 'payment'); e.target.value = ''; }} className="hidden" />
              <span className="p-1 text-slate-400 hover:text-emerald-600"><Upload className="w-3 h-3" /></span>
            </label>
          ) : (
            <span className="text-xs text-slate-300">-</span>
          )}
        </td>
        <td className="p-1 border-r">
          <div className="w-14 h-7 text-xs text-right bg-slate-100 border rounded px-2 flex items-center justify-end text-slate-600">
            {bankData.charges ? bankData.charges.toFixed(2) : '0.00'}
          </div>
        </td>
        <td className="p-1 border-r">
          <Input type="number" value={bankData.hidden_charges || ''} onChange={(e) => handlePaymentChange(entry.line_number, 'hidden_charges', e.target.value)}
            className="w-16 h-7 text-xs text-right" placeholder="0.00" disabled={!editing} />
        </td>
        <td className="p-1 border-r">
          <div className="w-16 h-7 text-xs text-right bg-amber-50 border rounded px-2 flex items-center justify-end text-amber-700 font-medium">
            {lineTotalCharges > 0 ? lineTotalCharges.toFixed(2) : '0.00'}
          </div>
        </td>
        <td className="p-1 border-r">
          <Input type="number" value={bankData.fund_movement || ''} onChange={(e) => handlePaymentChange(entry.line_number, 'fund_movement', e.target.value)}
            className="w-16 h-7 text-xs text-right" placeholder="0.00" disabled={!editing} />
        </td>
        <td className="p-1 border-r">
          <Input value={bankData.from_source || ''} onChange={(e) => handlePaymentChange(entry.line_number, 'from_source', e.target.value)}
            className="w-20 h-7 text-xs" placeholder="To..." disabled={!editing} />
        </td>
        <td className="p-1 border-r text-center">
          <div className="w-16 h-7 text-xs text-right bg-red-50 border rounded px-2 flex items-center justify-end font-semibold text-red-700">
            {lineTotal > 0 ? lineTotal.toLocaleString() : '-'}
          </div>
        </td>
        <td className="p-1 text-center">
          <div className="flex justify-center gap-1">
            {savingLines[`payment-${entry.line_number}`] ? (
              <Loader2 className="w-3 h-3 animate-spin text-emerald-600" />
            ) : !isAdmin ? (
              <span className="text-xs text-slate-400">View only</span>
            ) : entry.id ? (
              <>
                {editingPaymentLine === entry.line_number ? (
                  <button onClick={() => setEditingPaymentLine(null)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Check className="w-3 h-3" /></button>
                ) : (
                  <button onClick={() => setEditingPaymentLine(entry.line_number)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-3 h-3" /></button>
                )}
                <button onClick={() => deleteEntry(entry, 'payment')} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3" /></button>
              </>
            ) : (
              <span className="text-xs text-slate-400">Auto-save</span>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Access Bank Reconciliation</h1>
          <p className="text-slate-600">Daily deposits and payments reconciliation for Access Bank</p>
        </div>
      </div>

      {/* Date Navigation */}
      <div className="card p-4">
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => changeDate(-1)}><ChevronLeft className="w-4 h-4" /></Button>
          <div className="flex items-center gap-2">
            <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-40" />
            <span className="text-sm text-slate-600 font-medium">{formatDate(selectedDate)}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => changeDate(1)}><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}>Today</Button>
        </div>
        <div className="flex items-center justify-center gap-4 mt-3 flex-wrap">
          {previousMonthCarryForward !== 0 && (
            <div className={`px-3 py-2 rounded-lg text-sm ${previousMonthCarryForward >= 0 ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`}>
              B/F: {currencySymbol}{previousMonthCarryForward.toLocaleString()}
            </div>
          )}
          <div className="px-3 py-2 rounded-lg text-sm bg-emerald-50 text-emerald-700">
            Deposits MTD: {currencySymbol}{getDepositsMTDWithCarryForward().toLocaleString()}
          </div>
          <div className="px-3 py-2 rounded-lg text-sm bg-red-50 text-red-700">
            Payments MTD: {currencySymbol}{runningPaymentTotal.toLocaleString()}
          </div>
          <div className={`px-4 py-2 rounded-lg font-semibold ${calculateNetBalance() >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            Net: {currencySymbol}{calculateNetBalance().toLocaleString()}
          </div>
        </div>
      </div>

      {/* Two blocks side by side */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Deposits Block */}
        <div className="card overflow-hidden">
          <div className="bg-blue-600 text-white px-4 py-2 font-semibold">Deposits</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-blue-50">
                  <th className="p-1 border-r w-8">#</th>
                  <th className="p-1 border-r">Amount</th>
                  <th className="p-1 border-r">Description</th>
                  <th className="p-1 border-r">Receipt</th>
                  <th className="p-1 border-r">Bank Adj</th>
                  <th className="p-1 border-r">Fund Mvt</th>
                  <th className="p-1 border-r">From</th>
                  <th className="p-1 border-r">Total</th>
                  <th className="p-1 w-16">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="text-center py-4 text-slate-500">Loading...</td></tr>
                ) : depositEntries.map(renderDepositRow)}
              </tbody>
              <tfoot>
                <tr className="bg-blue-100 font-semibold">
                  <td className="p-2" colSpan={7}>Total</td>
                  <td className="p-2 text-right text-emerald-700">{currencySymbol}{calculateDepositTotal().toLocaleString()}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Payments/Charges Block */}
        <div className="card overflow-hidden">
          <div className="bg-purple-600 text-white px-4 py-2 font-semibold">Payments / Charges</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-purple-50">
                  <th className="p-1 border-r w-8">#</th>
                  <th className="p-1 border-r">Amount</th>
                  <th className="p-1 border-r">Description</th>
                  <th className="p-1 border-r">Receipt</th>
                  <th className="p-1 border-r">Charges</th>
                  <th className="p-1 border-r">Hidden Charges</th>
                  <th className="p-1 border-r">Total Charges</th>
                  <th className="p-1 border-r">Fund Mvt</th>
                  <th className="p-1 border-r">To</th>
                  <th className="p-1 border-r">Total</th>
                  <th className="p-1 w-16">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={11} className="text-center py-4 text-slate-500">Loading...</td></tr>
                ) : paymentEntries.map(renderPaymentRow)}
              </tbody>
              <tfoot>
                <tr className="bg-purple-100 font-semibold">
                  <td className="p-2" colSpan={4}>Total</td>
                  <td className="p-2 text-right text-red-600">{currencySymbol}{calculateChargesTotal().toLocaleString()}</td>
                  <td colSpan={4}></td>
                  <td className="p-2 text-right text-red-700">{currencySymbol}{calculatePaymentTotal().toLocaleString()}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <BankCategoryBreakdown bank="access_bank" selectedDate={selectedDate} />
      <UnmatchedDeposits />

      {/* View Receipt Dialog */}
      <Dialog open={viewReceiptOpen} onOpenChange={setViewReceiptOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Receipt</DialogTitle></DialogHeader>
          <div className="flex items-center justify-center p-4">
            {viewingReceipt && (
              viewingReceipt.startsWith('data:application/pdf') ? (
                <iframe src={viewingReceipt} className="w-full h-[500px] border rounded-lg" title="Receipt PDF" />
              ) : (
                <img src={viewingReceipt} alt="Receipt" className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg" />
              )
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Description Dialog */}
      <Dialog open={descriptionDialog.open} onOpenChange={(open) => !open && setDescriptionDialog({ open: false, type: '', lineNumber: null, value: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Description</DialogTitle></DialogHeader>
          <div className="py-4">
            <Textarea
              value={descriptionDialog.value}
              onChange={(e) => setDescriptionDialog(prev => ({ ...prev, value: e.target.value }))}
              placeholder="Enter description..."
              rows={4}
              className="resize-none"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDescriptionDialog({ open: false, type: '', lineNumber: null, value: '' })}>Cancel</Button>
            <Button onClick={saveDescription} className="bg-emerald-600 hover:bg-emerald-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccessBankReconciliation;
